import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEY as USER_PROFILE_QUERY_KEY } from '~/services/useUserProfileQuery';
import { UserProfile } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { ClientError } from '~/errors';
import { trackEvent, trackConversion } from '~/gtag';
import { tokenManager } from '~/services/TokenManager';

const log = createLogger('useLoginMutation');

export type LogInRequest = {
  idToken: string;
};

export type LogInResponse = {
  user: UserProfile;
  token: string;
};

export function useLogInMutation() {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: LogInRequest) => {
      log.debug('Logging in user', { request });
      const { user, token } = await httpClient.post<LogInResponse>('/auth/logInWithGoogle', request, {
        credentials: 'include',
      });
      log.info('Logged in user successfully', {
        userId: user.id,
      });
      return { user, token };
    },
    onSuccess: (data) => {
      log.debug('Setting token and user profile', { token: data.token, user: data.user });
      tokenManager.setToken(data.token);
      queryClient.setQueryData([USER_PROFILE_QUERY_KEY], { user: data.user });
      log.debug('Set token and user profile');
      Sentry.setUser({ id: data.user.id });
      log.debug('Set user in Sentry');
      trackEvent('login');
      trackConversion('login');
    },
    onError: (err: Error) => {
      if (err instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to log in user', {
          errorMessage: sanitizeTagValue(err.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    user: data?.user,
    token: data?.token,
  };
}
